import { importRemote as importRemoteUtility } from '@module-federation/utilities';

import { sendImportRemoteRuntimeEvent } from './events';
import { getFileNameFromURL } from '../helpers/getFileNameFromURL';
import { promiseWithRetries } from '../helpers/promiseWithRetries';
import { type RemoteInfo, getExoSkeletonRuntime } from '../internal';

const MAX_RETRIES = 3;

export interface ImportRemoteParams {
  scope: string;
  module: string;
  onRetry?: (params: {
    error: unknown;
    tryAttempt: number;
    retryName: string;
  }) => void;
}

const IMPORT_REMOTE_RETRY_TYPES = {
  GET_REMOTE: 'IMPORT_REMOTE_RETRY__GET_REMOTE',
  RESOLVE_REMOTE: 'IMPORT_REMOTE_RETRY__RESOLVE_REMOTE',
} as const;

export async function importRemote<T>({
  scope,
  module,
  onRetry = () => {},
}: ImportRemoteParams) {
  const performanceMark = window.performance.mark('importRemoteStart');
  const exoRuntime = getExoSkeletonRuntime();
  const scopeRuntimeInfo = exoRuntime.remotes?.[scope];

  if (!scopeRuntimeInfo) {
    throw new Error(
      `ExoSkeletonAPI Error: No remote found for scope "${scope}"`,
    );
  }
  let remote: RemoteInfo;

  try {
    const scopeRuntimeInfoResponse = await promiseWithRetries(
      () => scopeRuntimeInfo.get(),
      {
        retries: MAX_RETRIES,
        onRetry(error, tryAttempt) {
          return onRetry({
            error,
            tryAttempt,
            retryName: IMPORT_REMOTE_RETRY_TYPES.GET_REMOTE,
          });
        },
      },
    );

    remote = scopeRuntimeInfoResponse;
  } catch (cause) {
    throw new Error(
      `ExoSkeletonAPI Error: Failed to get the remote for scope "${scope}"\n${cause}`,
      { cause },
    );
  }

  const { url } = remote;
  const { urlWithoutFileName, fileName } = getFileNameFromURL(url);

  try {
    let importTryAttempt = 0;
    const resolvedPromise = await promiseWithRetries(
      ({ attempt: retries }) =>
        importRemoteUtility<T>({
          url: urlWithoutFileName,
          module,
          scope,
          bustRemoteEntryCache: retries > 1,
          remoteEntryFileName: fileName,
        }),
      {
        retries: MAX_RETRIES,
        onRetry(error, tryAttempt) {
          importTryAttempt = tryAttempt;

          return onRetry({
            error,
            tryAttempt,
            retryName: IMPORT_REMOTE_RETRY_TYPES.RESOLVE_REMOTE,
          });
        },
      },
    );

    void sendImportRemoteRuntimeEvent({
      scope,
      metadata: {
        url,
        retryAttempt: importTryAttempt,
        timeToImport: window.performance.now() - performanceMark.startTime,
      },
    });

    return resolvedPromise;
  } catch (cause) {
    throw new Error(
      `ExoSkeletonAPI Error: Failed to resolve remote "${module}" from scope "${scope}"\n${cause}`,
      { cause },
    );
  } finally {
    window.performance.clearMarks('importRemoteStart');
  }
}
